import { useCallback, useEffect, useRef, useState } from 'react'
import { normalizeScannedCode } from './barcode/normalize'

export type ProductScanState =
  | 'splash'
  | 'requesting-camera'
  | 'scanning'
  | 'checking'
  | 'matched'
  | 'not-matched'
  | 'camera-denied'
  | 'error'

export type ProductScanOutcome = 'MATCHED' | 'NOT_MATCHED' | 'ALREADY_SCANNED'

export type ProductScanApiProduct = {
  upc: string
  productName?: string
  brand?: string
  imageUrl?: string
}

export type ProductScanResult = {
  outcome: ProductScanOutcome
  product: ProductScanApiProduct | null
  scannedCode: string
}

// Thrown by the step's scan() for any non-2xx response, so an error body (no `outcome`) can
// never be mistaken for a match. 429 is the API's rate limit and gets its own copy.
export class ScanRequestError extends Error {
  status: number
  constructor(status: number) {
    super(`scan request failed: ${status}`)
    this.status = status
  }
}

export type ScanErrorKind = 'rate-limited' | 'failed'

export type ScanFn = (code: string) => Promise<ProductScanResult>

// Distinguishes *why* the camera isn't usable, per the AC — each gets its own copy in the
// scanner screen rather than one generic "camera unavailable" message.
export type CameraUnavailableReason = 'denied' | 'dismissed' | 'no-camera' | null

export type UseProductScanArgs = {
  scan: ScanFn
  requestCamera: () => Promise<{ granted: true } | { granted: false; reason: CameraUnavailableReason }>
  stopCamera: () => void
  onMatched: (result: ProductScanResult) => void
  onAlreadyScanned: (result: ProductScanResult) => void
  onNotMatched: (result: ProductScanResult) => void
}

export const useProductScan = ({
  scan,
  requestCamera,
  stopCamera,
  onMatched,
  onAlreadyScanned,
  onNotMatched,
}: UseProductScanArgs) => {
  const [state, setState] = useState<ProductScanState>('splash')
  const [cameraUnavailableReason, setCameraUnavailableReason] = useState<CameraUnavailableReason>(null)
  const [result, setResult] = useState<ProductScanResult | null>(null)
  const [errorKind, setErrorKind] = useState<ScanErrorKind>('failed')

  // Guards against the three real hazards named in the brief: a response landing after unmount,
  // a second decode firing while the first request is still in flight, and repeated decodes of
  // the same code (a barcode sits in frame across many decode ticks).
  const mountedRef = useRef(true)
  const inFlightRef = useRef(false)
  const lastSubmittedRef = useRef<string | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Camera stays live through a not-matched result (the design keeps the viewport visible with
  // the "not recognized" pill overlaid) and tears down once the flow leaves scanning for good:
  // a successful match, a denied/missing camera, or an error.
  useEffect(() => {
    if (state !== 'scanning' && state !== 'checking' && state !== 'not-matched') {
      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const startScan = useCallback(async () => {
    setCameraUnavailableReason(null)
    setState('requesting-camera')

    const permission = await requestCamera()
    if (!mountedRef.current) return

    if (!permission.granted) {
      setCameraUnavailableReason(permission.reason)
      setState('camera-denied')
      return
    }

    lastSubmittedRef.current = null
    setState('scanning')
  }, [requestCamera])

  const submitCode = useCallback(
    async (rawCode: string) => {
      const normalized = normalizeScannedCode(rawCode)
      if (!normalized) return
      if (inFlightRef.current) return
      if (normalized === lastSubmittedRef.current) return

      inFlightRef.current = true
      lastSubmittedRef.current = normalized
      setState('checking')

      try {
        const scanResult = await scan(normalized)
        if (!mountedRef.current) return

        setResult(scanResult)
        if (scanResult.outcome === 'NOT_MATCHED') {
          setState('not-matched')
          onNotMatched(scanResult)
        } else {
          setState('matched')
          if (scanResult.outcome === 'MATCHED') {
            onMatched(scanResult)
          } else {
            onAlreadyScanned(scanResult)
          }
        }
      } catch (e) {
        if (!mountedRef.current) return
        setErrorKind(e instanceof ScanRequestError && e.status === 429 ? 'rate-limited' : 'failed')
        setState('error')
      } finally {
        inFlightRef.current = false
      }
    },
    [scan, onMatched, onAlreadyScanned, onNotMatched],
  )

  const retry = useCallback(() => {
    lastSubmittedRef.current = null
    setResult(null)
    setState('scanning')
  }, [])

  return { state, cameraUnavailableReason, errorKind, result, startScan, submitCode, retry }
}
